import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import CampusOverviewHeader from './CampusOverviewHeader';
import CampusFilters from './CampusFilters';
import CampusSummaryCards from './CampusSummaryCards';
import CampusCard from './CampusCard';

const CampusOverview = () => {
  const [summary, setSummary] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('1w');
  
  const searchTimeoutRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchCampusOverview = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const params = {
        dateRange
      };
      
      if (search) {
        params.search = search;
      }
      
      const response = await axios.get(`${API_BASE_URL}/admin/campus/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params
      });

      setSummary(response.data.summary || null);
      setCampuses(response.data.campuses || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching campus overview:', err);
      setError('Failed to load campus data. Please try again.');
      setSummary(null);
      setCampuses([]);
    } finally {
      setLoading(false);
    }
  }, [search, dateRange, API_BASE_URL]);

  useEffect(() => {
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchCampusOverview();
    }, search ? 300 : 0); // 300ms delay for search, immediate for other changes
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchCampusOverview, search, dateRange]);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleDateRangeChange = useCallback((value) => {
    setDateRange(value);
  }, []);

  const handleDetailsClick = useCallback((campus) => {
    // TODO: Navigate to campus details page or open modal
    console.log('Campus details clicked:', campus);
  }, []);

  return (
    <div>
      <CampusOverviewHeader />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <CampusFilters
        onSearchChange={handleSearchChange}
        onDateRangeChange={handleDateRangeChange}
        searchValue={search}
        dateRangeValue={dateRange}
      />

      <CampusSummaryCards summary={summary} loading={loading} />

      {loading && campuses.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : campuses.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No campus data available.
        </div>
      ) : (
        <div className="row g-4">
          {campuses.map((campus) => (
            <div key={campus.campusId} className="col-12 col-md-6 col-xl-4">
              <CampusCard
                campus={campus}
                onDetailsClick={handleDetailsClick}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampusOverview;

